import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import useResponsive from "src/hooks/useResponsive";
import { PATH_APP } from "src/routes/paths";
import { isLeaseRoute } from "src/utils/mesaLeaseCore";
import { activeMesaId } from "src/redux/middleware/cartDraftMiddleware";
import {
  AUTO_SAVE_IDLE_MS,
  autoSaveFailText,
  autoSaveSnackText,
  decideAutoSave,
} from "src/utils/autoSave/autoSaveDraftCore";
import { isAnyCobroOpen } from "src/utils/autoSave/cobroOpenRegistry";
import { isAutoSaveInFlight, runAutoSave } from "src/utils/autoSave/autoSaveRunner";

// «Guardar automáticamente al salir de la mesa» (Ajustes → General,
// `autoSaveDraftOnLeave`). Se monta una vez en App. Con el ajuste OFF no
// hace nada. Disparadores:
//   - salir de la mesa: deseleccionar, volver al plano, cambiar de mesa o
//     ir a otra sección (misma regla de «estar dentro» que el lease);
//   - la app pasa a segundo plano (móvil bloqueado / cambio de app);
//   - 60 s sin tocar nada dentro de la mesa.
// La decisión (lease, sesión, cobro abierto, menús incompletos) está en
// utils/autoSave/autoSaveDraftCore; el guardado, en autoSaveRunner (misma
// petición que «Guardar»).

const isGridRoute = (pathname) => (pathname || "").startsWith(PATH_APP.tuCarta.tpv);

const validMesaId = (id) =>
  id != null && id !== -1 && id !== "-1" && id !== "00000000-0000-0000-0000-000000000000";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

function findMesa(state, mesaId) {
  const fromList = (state?.mesas?.mesas || []).find((m) => sameId(m?.id, mesaId));
  if (fromList) return fromList;
  const sel = state?.mesas?.mesa;
  return sameId(sel?.id, mesaId) ? sel : null;
}

function draftCart(state, mesaId) {
  const entry = (state?.product?.checkoutHistory || []).find((m) => sameId(m.idMesa, mesaId));
  return entry?.checkout?.cart || [];
}

export default function AutoSaveDraftWatcher() {
  const store = useStore();
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useResponsive("down", "sm");

  const enabled = useSelector((s) => !!s.authJwt?.user?.autoSaveDraftOnLeave);
  const selectedId = useSelector((s) => s.mesas?.selectedId);
  const pathname = location?.pathname;
  const inside = isLeaseRoute(pathname) && !(isMobile && isGridRoute(pathname));
  const target = inside && validMesaId(selectedId) ? selectedId : null;
  const lease = useSelector((s) => (target ? s.mesaLeases?.myLeases?.[target] || null : null));

  // Última foto de la mesa en la que se estaba (lease incluido): al salir,
  // el lease se suelta enseguida y la decisión se toma con esta foto.
  const snapRef = useRef(null);
  const prevTargetRef = useRef(target);
  const pathRef = useRef(pathname);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const notify = useCallback(
    (mesa) => ({
      onSuccess: (n) =>
        enqueueSnackbar(autoSaveSnackText(n, mesa), {
          variant: "info",
          autoHideDuration: 2500,
          preventDuplicate: true,
        }),
      onFailure: () =>
        enqueueSnackbar(autoSaveFailText(mesa), { variant: "warning", preventDuplicate: true }),
    }),
    [enqueueSnackbar],
  );

  const trySave = useCallback(
    ({ trigger, mesaId, leaseInfo, pathname: path, leaving }) => {
      if (!enabledRef.current) return;
      const state = store.getState();
      const mesa = findMesa(state, mesaId) || snapRef.current?.mesa;
      if (!mesa) return;
      // ¿Las líneas siguen en el carrito en pantalla o ya solo en el borrador?
      const cartIsMesa = sameId(activeMesaId(state), mesaId);
      const cart = state?.product?.checkout?.cart || [];
      const cartHasUnsent = cartIsMesa && cart.some((l) => !l.old);
      const source = cartHasUnsent || (!leaving && cartIsMesa) ? "cart" : "draft";
      const decision = decideAutoSave({
        enabled: true,
        trigger,
        mesaId,
        sessionId: mesa.sessionId,
        holdsLease: !!leaseInfo?.leaseId,
        leaseMode: leaseInfo?.mode || null,
        paymentOpen: isAnyCobroOpen(),
        pathname: path,
        inFlight: isAutoSaveInFlight(mesaId),
        closing: sameId(state?.mesas?.isClosingMesa, mesaId),
        cart: source === "cart" ? cart : draftCart(state, mesaId),
      });
      if (!decision.save) return;
      runAutoSave({
        dispatch,
        getState: store.getState,
        mesa,
        lines: decision.lines,
        keep: decision.keep,
        source,
        leaving,
        leaseId: leaseInfo?.leaseId || null,
        ...notify(mesa),
      });
    },
    [dispatch, store, notify],
  );

  // ── Salir de la mesa / cambiar de mesa ──────────────────────────────
  // Va ANTES que el efecto que actualiza la foto: aquí se ve la anterior.
  useEffect(() => {
    const prev = prevTargetRef.current;
    const prevPath = pathRef.current;
    prevTargetRef.current = target;
    pathRef.current = pathname;
    if (!prev || sameId(prev, target)) return;
    const snap = snapRef.current;
    if (!snap || !sameId(snap.mesaId, prev)) return;
    trySave({
      trigger: "leave",
      mesaId: prev,
      leaseInfo: snap.lease,
      // Ruta en la que se estaba (si se sale DESDE el cobro, no se guarda).
      pathname: prevPath,
      leaving: true,
    });
  }, [target, pathname, trySave]);

  useEffect(() => {
    snapRef.current = target
      ? { mesaId: target, lease, mesa: findMesa(store.getState(), target) }
      : null;
  });

  // Dentro de la mesa: guardado sobre el carrito en pantalla.
  const saveCurrent = useCallback(
    (trigger) => {
      if (!target) return;
      trySave({ trigger, mesaId: target, leaseInfo: lease, pathname, leaving: false });
    },
    [target, lease, pathname, trySave],
  );

  // ── App a segundo plano (móvil bloqueado, cambio de app/pestaña) ───
  useEffect(() => {
    if (!enabled || !target) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") saveCurrent("hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled, target, saveCurrent]);

  // ── 60 s sin interacción dentro de la mesa ──────────────────────────
  useEffect(() => {
    if (!enabled || !target) return undefined;
    let timer = null;
    const fire = () => {
      timer = null;
      const state = store.getState();
      // Con un diálogo abierto (editor de complementos, notas…) o en modo
      // borrar el camarero está trabajando: se espera a la próxima pausa.
      const dialogOpen = typeof document !== "undefined" && !!document.querySelector(".MuiDialog-root");
      if (dialogOpen || state?.product?.deleteMode) {
        arm();
        return;
      }
      saveCurrent("idle");
    };
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, AUTO_SAVE_IDLE_MS);
    };
    arm();
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, arm, { capture: true, passive: true }));
    return () => {
      if (timer) clearTimeout(timer);
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, arm, { capture: true }));
    };
  }, [enabled, target, saveCurrent, store]);

  return null;
}
